/**
 * Undo/Redo slice - manages undo/redo history
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

const MAX_HISTORY_STEPS = 15;

const initialState = {
  past: [], // Array of { originalAction, inverseAction } pairs
  future: [], // Array of { originalAction, inverseAction } pairs
};

const undoRedoSlice = createSlice({
  name: 'undoRedo',
  initialState,
  reducers: {
    // Add action pair to history
    addToHistory: (state, action) => {
      const { originalAction, inverseAction, originalActions, inverseActions } = action.payload;
      
      // Support both single action and transaction (multiple actions)
      if (originalActions && inverseActions) {
        // Transaction: multiple actions grouped together
        state.past.push({ originalActions, inverseActions });
      } else if (originalAction && inverseAction) {
        // Single action
        state.past.push({ originalAction, inverseAction });
      } else {
        return;
      }

      // Add to past, clear future
      state.future = [];

      // Limit history size
      if (state.past.length > MAX_HISTORY_STEPS) {
        state.past.shift();
      }
    },
    // Undo: move last past pair to future
    undo: (state) => {
      if (state.past.length === 0) return;
      const lastPair = state.past.pop();
      state.future.push(lastPair);
    },
    // Redo: move last future pair to past
    redo: (state) => {
      if (state.future.length === 0) return;
      const lastPair = state.future.pop();
      state.past.push(lastPair);
    },
    // Clear all history
    clearHistory: (state) => {
      state.past = [];
      state.future = [];
    },
    // Update the last pair's inverse action (used when ID changes after redo)
    updateLastInverseAction: (state, action) => {
      const { inverseAction, inverseActions } = action.payload;
      if (state.past.length > 0) {
        const lastPair = state.past[state.past.length - 1];
        if (inverseActions) {
          // Transaction
          lastPair.inverseActions = inverseActions;
        } else if (inverseAction) {
          // Single action
          lastPair.inverseAction = inverseAction;
        }
      }
    },
    // Replace the last entry with a transaction (used for grouping)
    replaceLastWithTransaction: (state, action) => {
      const { originalActions, inverseActions } = action.payload;
      if (state.past.length > 0 && originalActions && inverseActions) {
        state.past[state.past.length - 1] = { originalActions, inverseActions };
      }
    },
  },
});

export const { addToHistory, undo, redo, clearHistory, updateLastInverseAction, replaceLastWithTransaction } = undoRedoSlice.actions;

// Selectors
export const selectCanUndo = (state) => state.undoRedo.past.length > 0;
export const selectCanRedo = (state) => state.undoRedo.future.length > 0;
export const selectHistoryLength = (state) => state.undoRedo.past.length;

export default undoRedoSlice.reducer;

